import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { additionalInfoAPI } from '../services/api';

// Fields that are optional (not required)
const optionalFields = ['address_line2', 'county', 'bank_name', 'maiden_name', 'previous_surname'];

// Format sort code as XX-XX-XX
const formatSortCode = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  // Limit to 6 digits
  const limited = digits.slice(0, 6);
  // Format with hyphens
  if (limited.length <= 2) return limited;
  if (limited.length <= 4) return `${limited.slice(0, 2)}-${limited.slice(2)}`;
  return `${limited.slice(0, 2)}-${limited.slice(2, 4)}-${limited.slice(4)}`;
};

// Format account number (digits only, max 8)
const formatAccountNumber = (value) => {
  // Remove all non-digits and limit to 8
  return value.replace(/\D/g, '').slice(0, 8);
};

// Format National Insurance number as XX-XX-XX-XX-X (2 letters, 6 digits, 1 letter)
const formatNINumber = (value) => {
  // Remove spaces and hyphens, convert to uppercase
  const clean = value.replace(/[\s-]/g, '').toUpperCase();
  // Limit to 9 characters
  const limited = clean.slice(0, 9);

  // Format with spaces: XX XX XX XX X
  if (limited.length <= 2) return limited;
  if (limited.length <= 4) return `${limited.slice(0, 2)} ${limited.slice(2)}`;
  if (limited.length <= 6) return `${limited.slice(0, 2)} ${limited.slice(2, 4)} ${limited.slice(4)}`;
  if (limited.length <= 8) return `${limited.slice(0, 2)} ${limited.slice(2, 4)} ${limited.slice(4, 6)} ${limited.slice(6)}`;
  return `${limited.slice(0, 2)} ${limited.slice(2, 4)} ${limited.slice(4, 6)} ${limited.slice(6, 8)} ${limited.slice(8)}`;
};

const AdditionalInfo = () => {
  const { reference } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [preselectedClaim, setPreselectedClaim] = useState(null);
  const [formData, setFormData] = useState({
    claim_id: '',
    info_type: '',
    data: {}
  });

  const [addressLookup, setAddressLookup] = useState({ postcode: '', addresses: [], loading: false, error: '' });

  const infoTypes = [
    { value: 'ni_number', label: 'National Insurance Number', fields: ['ni_number'] },
    { value: 'address', label: 'Previous Address Information', fields: ['postcode', 'address_line1', 'address_line2', 'city', 'county'] },
    { value: 'maiden_name', label: 'Maiden Name / Previous Surname', fields: ['maiden_name', 'previous_surname'] },
    { value: 'investment_details', label: 'Investment Details', fields: ['policy_number'] },
    { value: 'sjp_partner', label: 'St. James\'s Place Partner Name', fields: ['partner_name'] },
    { value: 'bank_details', label: 'Bank Account Details', fields: ['account_name', 'sort_code', 'account_number', 'bank_name'] },
    { value: 'other', label: 'Other Information', fields: ['description', 'details'] }
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [claimsRes, submissionsRes] = await Promise.all([
        fetch('/api/claims', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json()),
        additionalInfoAPI.getAll()
      ]);

      const claimsData = claimsRes.data || claimsRes || [];
      setClaims(claimsData);
      setSubmissions(submissionsRes.data || submissionsRes || []);

      // If reference parameter is provided, find and preselect that claim
      if (reference && claimsData.length > 0) {
        const claim = claimsData.find(c => c.claim_reference === reference);
        if (claim) {
          setPreselectedClaim(claim);
          setFormData(prev => ({
            ...prev,
            claim_id: claim.id.toString()
          }));
        }
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInfoTypeChange = (e) => {
    const infoType = e.target.value;
    setFormData({
      ...formData,
      info_type: infoType,
      data: {}
    });
  };

  const handleFieldChange = (field, value) => {
    let formattedValue = value;

    // Apply formatting for specific fields
    if (field === 'sort_code') {
      formattedValue = formatSortCode(value);
    } else if (field === 'account_number') {
      formattedValue = formatAccountNumber(value);
    } else if (field === 'ni_number') {
      formattedValue = formatNINumber(value);
    }

    setFormData({
      ...formData,
      data: {
        ...formData.data,
        [field]: formattedValue
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.claim_id || !formData.info_type) {
      setError('Please select a claim and information type');
      return;
    }

    // For maiden_name type, require at least one of maiden_name or previous_surname
    if (formData.info_type === 'maiden_name') {
      const hasMaidenName = formData.data.maiden_name && formData.data.maiden_name.trim();
      const hasPreviousSurname = formData.data.previous_surname && formData.data.previous_surname.trim();
      if (!hasMaidenName && !hasPreviousSurname) {
        setError('Please provide either a Maiden Name or Previous Surname');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      await additionalInfoAPI.create({
        claim_id: formData.claim_id,
        info_type: formData.info_type,
        data: formData.data
      });

      setSuccess('Information submitted successfully! Our team will review and update your claim.');

      setFormData({
        claim_id: preselectedClaim ? preselectedClaim.id.toString() : '',
        info_type: '',
        data: {}
      });

      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const lookupPostcode = async () => {
    if (!addressLookup.postcode || addressLookup.postcode.trim().length < 5) {
      setAddressLookup(prev => ({ ...prev, error: 'Please enter a valid postcode' }));
      return;
    }

    setAddressLookup(prev => ({ ...prev, loading: true, error: '', addresses: [] }));

    try {
      const response = await fetch(
        `https://api.getAddress.io/find/${encodeURIComponent(addressLookup.postcode)}?api-key=D_hMYRHLIE6TNrk_YRlcBA45457`
      );

      if (!response.ok) {
        throw new Error('Postcode not found');
      }

      const data = await response.json();

      setAddressLookup(prev => ({
        ...prev,
        loading: false,
        addresses: data.addresses || []
      }));
    } catch (err) {
      setAddressLookup(prev => ({
        ...prev,
        loading: false,
        error: 'Unable to find addresses for this postcode'
      }));
    }
  };

  const selectAddress = (addressString) => {
    const parts = addressString.split(', ');

    // getAddress.io format: line_1, line_2, line_3, line_4, locality, town_or_city, county
    // Not all fields are always present, and postcode is not included (we already have it)
    let address1 = '';
    let address2 = '';
    let city = '';
    let county = '';

    if (parts.length >= 1) {
      address1 = parts[0]; // First line is always the building/street
    }

    if (parts.length >= 2) {
      address2 = parts[1]; // Second line if exists
    }

    // Last two parts are typically town/city and county
    if (parts.length >= 3) {
      county = parts[parts.length - 1]; // Last part is county
      city = parts[parts.length - 2]; // Second to last is town/city
    } else if (parts.length === 2) {
      city = parts[1]; // If only 2 parts, second is the city
    }

    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        address_line1: address1,
        address_line2: address2,
        city: city,
        county: county,
        postcode: addressLookup.postcode
      }
    }));

    setAddressLookup({ postcode: '', addresses: [], loading: false, error: '' });
  };

  const getFieldLabel = (field) => {
    const labels = {
      ni_number: 'National Insurance Number',
      address_line1: 'Address Line 1',
      address_line2: 'Address Line 2',
      city: 'City',
      county: 'County',
      postcode: 'Postcode',
      maiden_name: 'Maiden Name',
      previous_surname: 'Previous Surname',
      policy_number: 'Policy Number',
      partner_name: 'Partner Name',
      account_name: 'Account Holder Name',
      sort_code: 'Sort Code',
      account_number: 'Account Number',
      bank_name: 'Bank Name',
      description: 'Description',
      details: 'Details'
    };
    return labels[field] || field;
  };

  const getFieldPlaceholder = (field) => {
    const placeholders = {
      sort_code: '00-00-00',
      account_number: '12345678',
      ni_number: 'QQ 12 34 56 A'
    };
    return placeholders[field] || getFieldLabel(field);
  };

  const getFieldMaxLength = (field) => {
    const maxLengths = {
      sort_code: 8,       // XX-XX-XX = 8 chars
      account_number: 8,  // 8 digits
      ni_number: 13       // XX XX XX XX X = 13 chars with spaces
    };
    return maxLengths[field] || undefined;
  };

  const getFieldType = (field) => {
    if (field.includes('date')) return 'date';
    if (field.includes('amount')) return 'number';
    if (field === 'details') return 'textarea';
    return 'text';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedInfoType = infoTypes.find(t => t.value === formData.info_type);

  if (loading) {
    return (
      <Layout>
        <div>Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <h2>Additional Claim Information</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>Submit missing information to help us process your claim faster</p>

      {error && <div style={{ padding: '1rem', background: '#fee', color: '#c00', borderRadius: '4px', marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ padding: '1rem', background: '#d4edda', color: '#155724', borderRadius: '4px', marginBottom: '1rem' }}>{success}</div>}

      <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '2rem' }}>
        <h3 style={{ marginTop: 0 }}>Submit Missing Information</h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Select Claim *</label>
            {preselectedClaim && (
              <div style={{ padding: '0.75rem', background: '#e3f2fd', borderRadius: '4px', marginBottom: '0.5rem', color: '#1565c0' }}>
                Selected: {preselectedClaim.claim_reference} - {preselectedClaim.claim_type || 'Claim'}
              </div>
            )}
            <select
              value={formData.claim_id}
              onChange={(e) => setFormData({ ...formData, claim_id: e.target.value })}
              required
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">-- Select a claim --</option>
              {claims.map(claim => (
                <option key={claim.id} value={claim.id}>
                  {claim.claim_reference} - {claim.claim_type || 'Claim'}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>What information are you providing? *</label>
            <select
              value={formData.info_type}
              onChange={handleInfoTypeChange}
              required
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">-- Select information type --</option>
              {infoTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {selectedInfoType && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>{selectedInfoType.label}</h4>

              {formData.info_type === 'maiden_name' && (
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                  Please provide at least one of the fields below.
                </p>
              )}

              {formData.info_type === 'address' && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#e3f2fd', borderRadius: '4px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Find Address by Postcode
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={addressLookup.postcode}
                      onChange={(e) => setAddressLookup(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          lookupPostcode();
                        }
                      }}
                      placeholder="Enter postcode"
                      style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                    <button
                      type="button"
                      onClick={lookupPostcode}
                      disabled={addressLookup.loading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#4A90E2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: addressLookup.loading ? 'wait' : 'pointer'
                      }}
                    >
                      {addressLookup.loading ? 'Searching...' : 'Find Address'}
                    </button>
                  </div>
                  {addressLookup.error && (
                    <p style={{ color: '#c62828', fontSize: '0.9rem', margin: '0.5rem 0' }}>{addressLookup.error}</p>
                  )}
                  {addressLookup.addresses.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        Select Address:
                      </label>
                      <select
                        onChange={(e) => selectAddress(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        <option value="">-- Select an address --</option>
                        {addressLookup.addresses.map((addr, idx) => (
                          <option key={idx} value={addr}>{addr}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {selectedInfoType.fields.map(field => (
                <div key={field} style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    {getFieldLabel(field)}{!optionalFields.includes(field) && ' *'}
                  </label>
                  {getFieldType(field) === 'textarea' ? (
                    <textarea
                      value={formData.data[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      rows="4"
                      required={!optionalFields.includes(field)}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  ) : (
                    <input
                      type={getFieldType(field)}
                      value={formData.data[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      placeholder={getFieldPlaceholder(field)}
                      required={!optionalFields.includes(field)}
                      maxLength={getFieldMaxLength(field)}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '0.75rem 2rem',
              background: submitting ? '#ccc' : '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: '500'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Information'}
          </button>
        </form>
      </div>

      <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0 }}>Your Previous Submissions ({submissions.length})</h3>

        {submissions.length === 0 ? (
          <p style={{ color: '#666' }}>No submissions yet. Use the form above to submit missing information.</p>
        ) : (
          <div>
            {submissions.map(submission => (
              <div key={submission.id} style={{ padding: '1rem', borderBottom: '1px solid #eee', marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>
                  {infoTypes.find(t => t.value === submission.info_type)?.label || submission.info_type}
                </h4>
                <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                  <strong>Claim:</strong> {submission.claim_reference || 'N/A'}
                </p>
                <p style={{ margin: '0.5rem 0', color: '#888', fontSize: '0.9rem' }}>
                  <strong>Submitted:</strong> {formatDate(submission.submitted_at)}
                </p>
                {submission.reviewed_at && (
                  <p style={{ margin: '0.5rem 0', color: '#888', fontSize: '0.9rem' }}>
                    <strong>Reviewed:</strong> {formatDate(submission.reviewed_at)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdditionalInfo;
